import type { AppStockSetting, Warehouse } from '@erp/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, Combobox, FormField, Skeleton } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';

interface InventoryValue {
  allowNegativeStock?: boolean;
  /**
   * Whether zero-stock items disappear from the SELLING surfaces (storefront,
   * web register, offline till) while the guard above is on. Missing = hide,
   * which is what the storefront always did under the guard. Meaningless when
   * negative stock is allowed — everything is sellable then, so nothing hides.
   */
  hideZeroStock?: boolean;
  /**
   * Hide documents belonging to NO branch from users confined to one. Missing =
   * false = they still see unbranched rows, which is how branch scope shipped.
   */
  branchStrict?: boolean;
  /** THE tenant default warehouse — where stock lands/leaves when nothing chose one. */
  defaultWarehouseId?: string | null;
  /** Per-phone-app answers to the two rules above; missing = follow the shop. */
  apps?: { customer?: AppStockSetting; mandob?: AppStockSetting };
}

interface SettingsListItem {
  key: string;
  value: InventoryValue | unknown;
  updatedAt: string;
}

/** Everything this page edits, flat — one save writes the whole merged value. */
/** One app's answer: follow the shop, or its own answer to EACH rule. */
interface AppDraft {
  follow: boolean;
  allowNegative: boolean;
  hideZeros: boolean;
}

interface Draft {
  allowNegative: boolean;
  hideZeros: boolean;
  branchStrict: boolean;
  defaultWarehouseId: string;
  customer: AppDraft;
  mandob: AppDraft;
}

/**
 * Read whatever is stored for one app into the two switches.
 *
 * Three shapes reach this: nothing/'inherit' (follow the shop), one of the two
 * legacy mode words (which bundled the rules — shown as the pair they always
 * meant), or the explicit pair itself.
 */
function toAppDraft(stored: unknown, shop: { allowNegative: boolean; hideZeros: boolean }): AppDraft {
  if (stored === 'allow') return { follow: false, allowNegative: true, hideZeros: false };
  if (stored === 'block_hide') return { follow: false, allowNegative: false, hideZeros: true };
  if (stored && typeof stored === 'object') {
    const o = stored as { allowNegative?: unknown; hideZeros?: unknown };
    return {
      follow: false,
      allowNegative: typeof o.allowNegative === 'boolean' ? o.allowNegative : shop.allowNegative,
      hideZeros: typeof o.hideZeros === 'boolean' ? o.hideZeros : shop.hideZeros,
    };
  }
  return { follow: true, ...shop };
}

/**
 * Settings → Inventory.
 *
 * Under the generic key `inventory`:
 *  - allowNegativeStock: "sell on minus" (POS finalize reads it)
 *  - hideZeroStock: whether the guard also empties the shelves it refuses to sell
 *  - apps.customer / apps.mandob: the same question answered SEPARATELY for each
 *    phone app, because a shop can want its counter to oversell (the goods are in
 *    the customer's hands already) while a phone must only ever offer what is
 *    really there. 'inherit' — the default — leaves both following the shop.
 *  - defaultWarehouseId: المخزن الرئيسي — the warehouse everything falls back
 *    to when no explicit choice was made (bill receive, invoice ship, POS
 *    finalize, till sync, RMA restock). Empty = the first active warehouse by
 *    code, which is what "default" always meant before this control existed.
 *
 * Every save writes the WHOLE merged value — a per-field PUT would silently
 * wipe the sibling keys.
 */
export function InventorySettingsPage(): JSX.Element {
  const { t } = useTranslation('settings');
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState<Draft>({
    allowNegative: true,
    hideZeros: true,
    branchStrict: false,
    defaultWarehouseId: '',
    customer: { follow: true, allowNegative: true, hideZeros: true },
    mandob: { follow: true, allowNegative: true, hideZeros: true },
  });
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = async (): Promise<void> => {
    const [res, wh] = await Promise.all([
      api().get<{ items: SettingsListItem[] }>('/settings'),
      api().get<{ items: Warehouse[] }>('/warehouses'),
    ]);
    const inv = res.data.items.find((s) => s.key === 'inventory')?.value as InventoryValue | undefined;
    const allowNegative = inv?.allowNegativeStock !== false; // default true
    const hideZeros = inv?.hideZeroStock !== false; // default hide (the guard's historical behaviour)
    // What the shop's own pair RESOLVES to, which is what an app inherits — the
    // hide rule only bites when the shop refuses to oversell.
    const shop = { allowNegative, hideZeros: !allowNegative && hideZeros };
    setDraft({
      allowNegative,
      hideZeros,
      branchStrict: inv?.branchStrict === true,
      defaultWarehouseId: inv?.defaultWarehouseId ?? '',
      customer: toAppDraft(inv?.apps?.customer, shop),
      mandob: toAppDraft(inv?.apps?.mandob, shop),
    });
    setWarehouses(wh.data.items.filter((w) => w.isActive));
    setLoaded(true);
  };

  useEffect(() => {
    void reload();
  }, []);

  const save = async (patch: Partial<Draft>): Promise<void> => {
    setSaving(true);
    setError(null);
    setSavedNote(null);
    const prev = draft;
    const next: Draft = { ...draft, ...patch };
    const value: InventoryValue = {
      allowNegativeStock: next.allowNegative,
      hideZeroStock: next.hideZeros,
      branchStrict: next.branchStrict,
      defaultWarehouseId: next.defaultWarehouseId || null,
      // 'inherit' when following, otherwise the explicit pair — so an app that
      // follows the shop keeps following it as the shop's own rules change.
      apps: {
        customer: next.customer.follow ? 'inherit' : { allowNegative: next.customer.allowNegative, hideZeros: next.customer.hideZeros },
        mandob: next.mandob.follow ? 'inherit' : { allowNegative: next.mandob.allowNegative, hideZeros: next.mandob.hideZeros },
      },
    };
    setDraft(next); // optimistic — reverted below on failure
    try {
      await api().put('/settings/inventory', { value });
      setSavedNote(t('inventory.saved'));
    } catch {
      setDraft(prev);
      setError(t('inventory.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <Card>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-20 w-full" />
      </Card>
    );
  }

  return (
    <Card>
      <h2 className="mb-2 text-lg font-semibold">{t('inventory.title')}</h2>
      <p className="mb-6 text-sm text-fg-muted">{t('inventory.description')}</p>

      <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
        <div className="min-w-0">
          <p className="text-sm font-medium">{t('inventory.allowNegative.label')}</p>
          <p className="mt-1 text-sm text-fg-muted">{t('inventory.allowNegative.help')}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={draft.allowNegative}
          aria-label={t('inventory.allowNegative.label')}
          disabled={saving}
          onClick={() => void save({ allowNegative: !draft.allowNegative })}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition disabled:opacity-50 ${
            draft.allowNegative ? 'bg-brand' : 'bg-border'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
              draft.allowNegative ? 'translate-x-5' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Only meaningful while the guard above refuses to oversell: with
          negative stock allowed everything is sellable, so nothing hides. */}
      {!draft.allowNegative && (
        <div className="ms-6 mt-2 flex items-start justify-between gap-4 rounded-lg border border-dashed border-border p-4">
          <div className="min-w-0">
            <p className="text-sm font-medium">{t('inventory.hideZeros.label', { defaultValue: 'Hide zero-stock items' })}</p>
            <p className="mt-1 text-sm text-fg-muted">
              {t('inventory.hideZeros.help', {
                defaultValue: 'Products with nothing on hand disappear from the storefront, the register and the cashier terminal. Turn off to keep showing them — selling them will still be refused.',
              })}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={draft.hideZeros}
            aria-label={t('inventory.hideZeros.label', { defaultValue: 'Hide zero-stock items' })}
            disabled={saving}
            onClick={() => void save({ hideZeros: !draft.hideZeros })}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition disabled:opacity-50 ${
              draft.hideZeros ? 'bg-brand' : 'bg-border'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                draft.hideZeros ? 'translate-x-5' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      )}

      {/* The phone apps answer the same question for themselves. Each selector is
          independent: a shop can keep selling on minus everywhere and still let
          only what is on the shelf reach the customer app. */}
      <div className="mt-4 rounded-lg border border-border p-4">
        <p className="text-sm font-medium">{t('inventory.apps.title')}</p>
        <p className="mt-1 text-sm text-fg-muted">{t('inventory.apps.description')}</p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {([
            { key: 'customer' as const, id: 'inv-app-customer' },
            { key: 'mandob' as const, id: 'inv-app-mandob' },
          ]).map(({ key, id }) => {
            const app = draft[key];
            return (
              <div key={key} className="rounded-lg border border-border p-3">
                <p className="text-sm font-medium">{t(`inventory.apps.${key}.label`)}</p>
                <p className="mt-1 text-xs text-fg-muted">{t(`inventory.apps.${key}.help`)}</p>
                <label className="mt-3 flex items-center gap-2 text-sm">
                  <input
                    id={id}
                    type="checkbox"
                    checked={app.follow}
                    disabled={saving}
                    onChange={(e) => void save({ [key]: { ...app, follow: e.target.checked } } as Partial<Draft>)}
                  />
                  <span>{t('inventory.apps.modes.inherit')}</span>
                </label>
                {/* Off the leash: the app answers the SAME two questions the shop
                    does, each on its own — that is the whole point of a per-app
                    override, and the single dropdown could only offer them in
                    pairs. */}
                {!app.follow && (
                  <div className="mt-3 flex flex-col gap-2 border-t border-border pt-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={app.allowNegative}
                        disabled={saving}
                        onChange={(e) => void save({ [key]: { ...app, allowNegative: e.target.checked } } as Partial<Draft>)}
                      />
                      <span>{t('inventory.allowNegative.label')}</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={app.hideZeros}
                        disabled={saving}
                        onChange={(e) => void save({ [key]: { ...app, hideZeros: e.target.checked } } as Partial<Draft>)}
                      />
                      <span>{t('inventory.hideZeros.label')}</span>
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-border p-4">
        <FormField
          id="inv-default-wh"
          label={t('inventory.defaultWarehouse.label', { defaultValue: 'Default warehouse' })}
          helperText={t('inventory.defaultWarehouse.help', {
            defaultValue: 'Bills receive into it and sales ship from it whenever no warehouse was chosen on the document, the till or the rep app.',
          })}
        >
          <Combobox
            id="inv-default-wh"
            options={warehouses.map((w) => ({ value: w.id, label: `${w.code} — ${w.name}` }))}
            value={draft.defaultWarehouseId || undefined}
            onChange={(v) => void save({ defaultWarehouseId: v ?? '' })}
            placeholder={t('inventory.defaultWarehouse.placeholder', { defaultValue: 'First warehouse by code' })}
            ariaLabel={t('inventory.defaultWarehouse.label', { defaultValue: 'Default warehouse' })}
          />
        </FormField>
      </div>

      <div className="mt-4 rounded-lg border border-border p-4">
        <FormField
          id="inv-branch-strict"
          label={t('inventory.branchStrict.label', { defaultValue: 'Branch isolation' })}
          helperText={t('inventory.branchStrict.help', {
            defaultValue:
              'A user limited to a branch normally also sees documents that belong to no branch, so records written before you had branches do not disappear. Tick this once every branch is set up and they will see only their own. Owners always see everything.',
          })}
        >
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              id="inv-branch-strict"
              type="checkbox"
              className="h-4 w-4"
              checked={draft.branchStrict}
              disabled={saving}
              onChange={(e) => void save({ branchStrict: e.target.checked })}
            />
            <span>
              {t('inventory.branchStrict.toggle', {
                defaultValue: 'Branch users see only their own branch',
              })}
            </span>
          </label>
        </FormField>
      </div>

      {error && (
        <p className="mt-4 text-sm text-danger" role="alert">
          {error}
        </p>
      )}
      {savedNote && (
        <p className="mt-4 text-sm text-success" role="status">
          {savedNote}
        </p>
      )}
    </Card>
  );
}

